/*
 * preview-sync.js — WHERE in the file the preview is sounding, measured.
 *
 * The preview plays the original through <audio>/<video> and silences the
 * censored stretches in an AudioWorklet (preview-gate-worklet.js). The gate is
 * sample-accurate only if it knows which moment OF THE FILE each sample in the
 * graph is. Nothing in the platform says that: `currentTime` of the element
 * is the element's own clock, its decoder trims MP3 priming its own way, and a
 * seek lands where the demuxer guesses.
 *
 * Until 09.10.2026 the gate decided from `currentTime + outputLatency + 29 ms`
 * (the 07.08 «lookahead»). That added the OUTPUT DEVICE's latency to an
 * alignment that lives INSIDE one stream — the word and its silence leave the
 * graph together and reach the ear together, so the device delays both alike.
 * The preview therefore muted EARLY by the device latency: ~56 ms on a desktop
 * card, ~110 ms on Ангелина's headset (chat 640, measured on her own screen
 * recording: the preview's silence began 60 ms and ended 105 ms before the
 * file's). She placed 297 censors by that preview — and the file, cut exactly,
 * let the start of every word through.
 *
 * So the position is not estimated, it is MEASURED: the worklet hands back a
 * short tap of what is entering the gate, this module finds that tap in the
 * 16 kHz PCM the tool already holds (the same PCM the words, the waveform and
 * the export live on), and the gate is driven by that mapping. Whatever the
 * browser, the decoder, the seek or the device do, the preview and the file
 * share one timeline by construction.
 *
 * Pure functions (no DOM): unit-tested by tests/js/profanity-preview-sync.test.mjs.
 */
(function (root) {
    'use strict';

    var SR = 16000;

    /* ---- FFT (radix-2, in place, complex) ---- */
    function fft(re, im, inverse) {
        var n = re.length, i, j, k;
        for (i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                var tr = re[i]; re[i] = re[j]; re[j] = tr;
                var ti = im[i]; im[i] = im[j]; im[j] = ti;
            }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = 2 * Math.PI / len * (inverse ? 1 : -1);
            var wr = Math.cos(ang), wi = Math.sin(ang);
            var half = len >> 1;
            for (i = 0; i < n; i += len) {
                var cr = 1, ci = 0;
                for (k = 0; k < half; k++) {
                    var a = i + k, b = a + half;
                    var xr = re[b] * cr - im[b] * ci, xi = re[b] * ci + im[b] * cr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi;
                    re[a] += xr; im[a] += xi;
                    var nr = cr * wr - ci * wi;
                    ci = cr * wi + ci * wr; cr = nr;
                }
            }
        }
        if (inverse) for (i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
    }
    function pow2(n) { var p = 1; while (p < n) p <<= 1; return p; }

    /* Graph-rate mono → 16 kHz. A box average over the source span of each
       output sample: a cheap low-pass that keeps the speech band the
       correlation needs and does not alias the hiss above 8 kHz into it. */
    function to16k(x, sr) {
        var step = sr / SR, n = Math.floor(x.length / step), out = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var a = i * step, b = a + step, ia = Math.floor(a), ib = Math.min(x.length, Math.ceil(b));
            var s = 0, w = 0;
            for (var j = ia; j < ib; j++) {
                var lo = Math.max(a, j), hi = Math.min(b, j + 1), f = hi - lo;
                if (f > 0) { s += x[j] * f; w += f; }
            }
            out[i] = w > 0 ? s / w : 0;
        }
        return out;
    }

    function rms(x) {
        var s = 0;
        for (var i = 0; i < x.length; i++) s += x[i] * x[i];
        return Math.sqrt(s / Math.max(1, x.length));
    }

    /* Find `tap` (16 kHz, float) in `pcm` (Int16Array or Float32Array, 16 kHz)
     * within [lo, hi) sample positions of its START. Normalised
     * cross-correlation, so loudness (the preview's volume, the decoder's
     * gain) does not matter. Returns { pos (fractional sample), ncc, second }
     * — `second` is the best peak at least 5 ms away, the ambiguity of the
     * match (a repeated beat or a sustained tone looks alike everywhere). */
    function locate(pcm, tap, lo, hi) {
        var scale = (pcm instanceof Int16Array) ? 1 / 32768 : 1;
        var m = tap.length;
        lo = Math.max(0, Math.floor(lo));
        hi = Math.min(pcm.length - m, Math.ceil(hi));
        if (m < 64 || hi <= lo) return null;
        var span = hi - lo + m;
        var n = pow2(span + m);
        var ar = new Float64Array(n), ai = new Float64Array(n);
        var br = new Float64Array(n), bi = new Float64Array(n);
        var tMean = 0, i;
        for (i = 0; i < m; i++) tMean += tap[i];
        tMean /= m;
        var tEn = 0;
        for (i = 0; i < m; i++) { var v = tap[i] - tMean; br[i] = v; tEn += v * v; }
        for (i = 0; i < span; i++) ar[i] = pcm[lo + i] * scale;
        fft(ar, ai, false); fft(br, bi, false);
        for (i = 0; i < n; i++) {
            // a · conj(b)
            var r = ar[i] * br[i] + ai[i] * bi[i];
            var q = ai[i] * br[i] - ar[i] * bi[i];
            ar[i] = r; ai[i] = q;
        }
        fft(ar, ai, true);
        // sliding energy / sum of the pcm window, for the normalisation
        var cs = new Float64Array(span + 1), cs2 = new Float64Array(span + 1);
        for (i = 0; i < span; i++) {
            var p = pcm[lo + i] * scale;
            cs[i + 1] = cs[i] + p; cs2[i + 1] = cs2[i] + p * p;
        }
        var count = hi - lo + 1, ncc = new Float64Array(count);
        var best = -2, bestK = -1;
        for (var k = 0; k < count; k++) {
            var sum = cs[k + m] - cs[k], sum2 = cs2[k + m] - cs2[k];
            var en = sum2 - sum * sum / m;
            var c = (en > 1e-12 && tEn > 1e-12) ? ar[k] / Math.sqrt(en * tEn) : 0;
            ncc[k] = c;
            if (c > best) { best = c; bestK = k; }
        }
        if (bestK < 0) return null;
        var second = -2, guard = Math.round(0.005 * SR);
        for (k = 0; k < count; k++) {
            if (Math.abs(k - bestK) > guard && ncc[k] > second) second = ncc[k];
        }
        // parabolic refinement: the peak between samples (62.5 µs at 16 kHz)
        var off = 0;
        if (bestK > 0 && bestK < count - 1) {
            var y0 = ncc[bestK - 1], y1 = ncc[bestK], y2 = ncc[bestK + 1];
            var d = y0 - 2 * y1 + y2;
            if (d < 0) off = Math.max(-0.5, Math.min(0.5, 0.5 * (y0 - y2) / d));
        }
        return { pos: lo + bestK + off, ncc: best, second: second };
    }

    /* The verdict for one tap: where in the FILE its first sample is.
     *   tap      — Float32Array, mono, at graph rate `sr`
     *   pcm      — the tool's 16 kHz PCM
     *   expectS  — where the provisional mapping thinks it starts
     *   rate     — playbackRate (media seconds per graph second)
     * At rate 1 the whole tap is matched at once. Faster, the element
     * time-stretches with pitch kept (WSOLA): the waveform survives only
     * grain by grain, so short pieces are matched separately and the start
     * is their median. Returns { startS, ncc } or null when the tap is too
     * quiet or too ambiguous to trust (silence, a sustained note). */
    var MIN_RMS = 0.003;      // ≈ −50 dBFS: below this there is nothing to recognise
    var MIN_NCC = 0.6;
    var MIN_MARGIN = 0.08;    // best must beat the next candidate by this much
    function measure(tap, sr, pcm, expectS, rate, opts) {
        opts = opts || {};
        var search = opts.searchS || 0.6;
        rate = rate || 1;
        var t16 = to16k(tap, sr);
        if (rms(t16) < MIN_RMS) return null;
        var expect = expectS * SR;
        if (Math.abs(rate - 1) < 1e-3) {
            var r = locate(pcm, t16, expect - search * SR, expect + search * SR);
            if (!r || r.ncc < MIN_NCC || r.ncc - r.second < MIN_MARGIN) return null;
            return { startS: r.pos / SR, ncc: r.ncc };
        }
        var grain = Math.round(0.06 * SR), starts = [], nccs = [];
        for (var g = 0; g + grain <= t16.length; g += grain) {
            var piece = t16.subarray(g, g + grain);
            if (rms(piece) < MIN_RMS) continue;
            var at = expect + g * rate;          // this grain's media position, provisionally
            var q = locate(pcm, piece, at - search * SR, at + search * SR);
            if (!q || q.ncc < MIN_NCC) continue;
            starts.push(q.pos / SR - (g / SR) * rate);
            nccs.push(q.ncc);
        }
        if (starts.length < 2) return null;
        starts.sort(function (a, b) { return a - b; });
        nccs.sort(function (a, b) { return a - b; });
        var med = starts[starts.length >> 1];
        // grains that disagree mean the stretch fooled the match — no verdict
        var near = starts.filter(function (s) { return Math.abs(s - med) < 0.012; }).length;
        if (near < Math.ceil(starts.length * 0.6)) return null;
        return { startS: med, ncc: nccs[nccs.length >> 1] };
    }

    /* How the PRE-09.10 preview missed on this device, in seconds of the
     * file — so work placed BY EAR against it can be put where it was heard.
     *   oldLookahead = min(0.7, outputLatency + 0.029) — what it added;
     *   lag          = measured (graph position − currentTime) at rate 1.
     * Its gate therefore switched on (oldLookahead − lag) BEFORE the region
     * and faded with a 6 ms time constant decided once per frame (~8 ms):
     * the silence was heard to start ~25 ms after the switch and to end ~5 ms
     * after the release. The gate it is replaced by is the file's own: 30 ms
     * ramps that meet the bounds. Matching what was HEARD (−20 dB points):
     *     start' = start − bias + 0.025,   end' = end − bias + 0.005. */
    function legacyShift(outputLatency, lagS) {
        var oldLookahead = Math.min(0.7, (outputLatency > 0 ? outputLatency : 0) + 0.017 + 0.012);
        var bias = oldLookahead - (lagS || 0);
        return { bias: bias, start: -bias + 0.025, end: -bias + 0.005 };
    }

    var api = { SR: SR, fft: fft, to16k: to16k, rms: rms, locate: locate, measure: measure, legacyShift: legacyShift,
        MIN_NCC: MIN_NCC, MIN_RMS: MIN_RMS };
    root.ProfanityPreviewSync = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
